import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import ExportDropdown from "../../../Components/Export"
import Filters from "../../../Components/Filters"
import GlobalFilterTestData from "../../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../../Components/ManageColumns"
import Table from "../../../Components/Table"
import { getBaseColumns } from "../../../Components/Table/baseColumns"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { defaultFiltersByFolders, serializeData, useQuery } from "../../../Utils/utils"
import { useUpdatePreferences } from "../../User/hooks/useUpdatePreferences"
import RequestsCreators, { selectRequestResult } from "../reducer"
import { getActionColumn } from "../RequestsEnrollments/components/Table/columns"
import { getColumns, getDefaultColumn } from "./components/Table/columns"
import Creators, {
  REQUESTS_AUTO_REFUND_PREFERENCES_VERSION,
  selectAreFiltersDefault
} from "./reducer"

const pageSize = 50

const { Text } = Typography

const defaultFiltersByFolder = defaultFiltersByFolders?.["auto_refund"]

const tableProp = { sequence_number: { width: 80 } }

const RequestsAutoRefundPage = ({
  isMobile,
  businessAccountId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  test_data,
  filterOptions,
  exportFetching,
  exportResult,
  saveResult,
  statistics,
  ...props
}) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  let query = useQuery(search)
  let folder = query.get("folder")
  const { notification, modal } = App.useApp()
  const { t } = useTranslation()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const isFiltersDefault = useSelector((state) => selectAreFiltersDefault(state, folder))
  const requestResult = useSelector(selectRequestResult)

  const { count, results } = result

  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")
  const filterOptionsStatusHash = filterOptions?.["request_status"].join("")

  const preferencesData = useMemo(
    () => ({
      version: REQUESTS_AUTO_REFUND_PREFERENCES_VERSION,
      filters,
      columnsAdmin,
      visibleFiltersAdmin
    }),
    [filters, columnsAdmin, visibleFiltersAdmin]
  )
  useUpdatePreferences("requests-auto-refund", preferencesData)

  const statisticsFolder = statistics?.["auto_refund_counter"]?.[folder || "all"] || 0

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const handleClearFilters = () => {
    const newFilters = folder
      ? defaultFiltersByFolder[folder]
      : { request_status: "all", is_archived: null }
    props.clearFilters(newFilters)
  }

  useEffect(() => {
    document.title = "Requests Auto-refund - Chargeback"
    props.getFilterOptions()
  }, [])

  useEffect(() => {
    if (folder) {
      props.changeFilters(defaultFiltersByFolder[folder])
      if (pageNumber !== 1) setPageNumber(1)
    } else {
      props.changeFilters({ request_status: "all", is_archived: null })
    }
  }, [folder])

  const filterOptionsModifiedByFolder = useMemo(
    () => ({
      ...filterOptions,
      request_status:
        !folder || defaultFiltersByFolder[folder]?.request_status === "all"
          ? filterOptions?.request_status
          : defaultFiltersByFolder[folder]?.request_status
    }),
    [filterOptions, folder]
  )

  const prevTestDataRef = useRef(test_data)

  useEffect(() => {
    if (saveResult || requestResult) return

    const testDataChanged = prevTestDataRef.current !== test_data
    if (testDataChanged) {
      prevTestDataRef.current = test_data
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    const serializeFilters = serializeData({
      ...filters,
      ...(folder &&
        (!filters["request_status"]?.length || filters["request_status"] === "all") && {
          request_status: defaultFiltersByFolder[folder]?.request_status
        })
    })

    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      test_data,
      ...(columns.length && {
        columns: ["id", "request_status", "is_archived", ...columns].join(",")
      })
    }
    props.getInfo({ ...data })
  }, [
    businessAccountId,
    ordering,
    pageNumber,
    filters,
    test_data,
    myColumnsLength,
    saveResult,
    requestResult
  ])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: "Exporting",
        description:
          t(`${exportResult?.message}`) || t("Business accounts will be downloaded shortly."),
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  useEffect(() => {
    if (saveResult) {
      if (saveResult === "archived") {
        notification.success({
          message: "Archiving complete",
          ...notificationConfig,
          style: { minWidth: "max-content" }
        })
      }
      props.saveResultReset(null)
    }
  }, [saveResult])

  const tableSettings = useMemo(() => {
    const viewportHeight = window.innerHeight
    const fixedPixels = 72 + 24 + 32 + 24 + 43 + (16 + 32 + 16) + 16
    return {
      scroll: {
        x: myColumnsLength ? 250 * myColumnsLength : "100%",
        y: viewportHeight - fixedPixels,
        //x: count ? 'max-content' : (230 * myColumns.filter(({active}) => active).length),
        //y: 'calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px) - 16px)',
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !statisticsFolder ? (
          <EmptyComponent type="requests_auto_refunds_admin" />
        ) : (
          <EmptyComponent type="search">
            <Button
              className="flex"
              icon={<Icon type={"menu_search"} />}
              onClick={handleClearFilters}
            >
              Clear search
            </Button>
          </EmptyComponent>
        )
      }
    }
  }, [myColumnsLength, count, pageNumber, statisticsFolder])

  const getTableColumns = useMemo(() => {
    const handleChangeStatus = ({ id, newStatus }) => {
      props.updateStatus({ id, request_status: newStatus })
    }

    let options = filterOptions?.["request_status"] || []

    let allColumns = {
      ...getBaseColumns({ onSelectRow: handleSelectRow, isAdmin: true, props: tableProp }),
      ...getColumns({ options, handleChangeStatus })
    }
    return [
      allColumns["status"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash, filterOptionsStatusHash, search])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
  }, [])

  function handleSelectRow(data, type) {
    let business_account_id = data?.business_account?.id
    if (type === "business_account") {
      if (business_account_id) {
        navigate(`/business_settings/enrollment?${new URLSearchParams({ business_account_id })}`)
      }
    } else if (type === "dashboard") {
      navigate(`/admin/dashboard?business_account_id=${business_account_id}`)
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,

        title: "Confirm deleting",
        content: "Are you sure you want to delete this request? This action can NOT be undone.",

        cancelText: "Cancel",
        okText: "Delete",
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        onOk() {
          props.deleteRequest(data.id)
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    } else if (type === "archive") {
      props.updateStatus({ id: data.id, is_archived: !data?.["is_archived"] })
    } else if (type === "add_comment") {
      props.selectRequestAddComment(data)
      const searchParams = new URLSearchParams(search)
      searchParams.set("add_comment", data?.id)
      navigate(`?${searchParams.toString()}`)
    }
  }

  const handleExport = useCallback(
    (type) => {
      const serializeFilters = serializeData(filters)
      props.getExport({
        file_name: "Requests Auto-refund report",
        ...(type !== "all" && serializeFilters),
        ...(type !== "all" ? { test_data } : {})
      })
    },
    [filters, test_data]
  )

  return (
    <div className="requests-page">
      <Flex gap={32} align="center" justify="space-between" style={{ margin: "0 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptionsModifiedByFolder}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          filtersType={"requests"}
          showClearBtn={!isFiltersDefault}
          onClearFilters={handleClearFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData />
          <ExportDropdown loading={exportFetching} onExport={handleExport} isAdmin={true} />

          <ManageColumn myColumns={myColumns} onChangeColumns={handleChangeColumns} />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        virtual={true}
        //withOutPagination={true}
        locale={!props.fetchingFilterOptions ? tableSettings.locale : {}}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.requestsAutoRefund.visibleFiltersAdmin,

  defaultColumnsAdmin: state.requestsAutoRefund.defaultColumnsAdmin,

  columns: state.requestsAutoRefund.columns,
  columnsAdmin: state.requestsAutoRefund.columnsAdmin,

  fetching: state.requestsAutoRefund.fetching,
  result: state.requestsAutoRefund.result,

  filters: state.requestsAutoRefund.filters,
  filterOptions: state.requestsAutoRefund.filterOptions,
  fetchingFilterOptions: state.requestsAutoRefund.fetchingFilterOptions,

  exportFetching: state.requestsAutoRefund.exportFetching,
  exportResult: state.requestsAutoRefund.exportResult,

  saveResult: state.requestsAutoRefund.saveResult,

  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getFilterOptions: () => dispatch(Creators.getRequestsAutoRefundFilterOptionsRequest()),

  getInfo: (params) => dispatch(Creators.getRequestsAutoRefundRequest(params)),
  deleteRequest: (id) => dispatch(Creators.deleteRequestsAutoRefundRequest(id)),
  updateStatus: (params) => dispatch(Creators.updateRequestsAutoRefundRequest(params)),
  getExport: (businessId) => dispatch(Creators.getRequestsAutoRefundExportRequest(businessId)),
  clearExportResult: () => dispatch(Creators.requestsAutoRefundSetProp("exportResult", null)),
  changeColumns: (key, columns) => dispatch(Creators.requestsAutoRefundSetProp(key, columns)),
  changeFilters: (filters) => dispatch(Creators.requestsAutoRefundSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.requestsAutoRefundSetProp(visibleFiltersType, filters)),
  clearFilters: (data) => dispatch(Creators.clearRequestsAutoRefundFilters(data)),
  saveResultReset: (value) => dispatch(Creators.requestsAutoRefundSetProp("saveResult", value)),

  selectRequestAddComment: (request) =>
    dispatch(RequestsCreators.requestsSetProp("selectedRequest", request))
})

const RequestsAutoRefund = connect(mapStateToProps, mapDispatchToProps)(RequestsAutoRefundPage)

export default React.memo(RequestsAutoRefund)
